import type { UTCDate } from '@date-fns/utc';
import { addMonths, format, isAfter, parse } from 'date-fns';

import type { CompositionV3Filters } from '@revelio/data-access';

import { getFormattedDateString } from '../dateUtils';

/**
 * Builds month labels (`yyyy-MM`) for an inclusive `yyyy-MM` range.
 * Composition V3 timeseries points use absolute month indices into this timeline
 * (same convention as legacy `composition_v2.refs.months`).
 */
export const buildMonthRefsInclusive = (
  startYYYYMM: string,
  endYYYYMM: string
): string[] => {
  const start = parse(startYYYYMM, 'yyyy-MM', new Date());
  const end = parse(endYYYYMM, 'yyyy-MM', new Date());
  const months: string[] = [];
  let cur = start;
  while (!isAfter(cur, end)) {
    months.push(format(cur, 'yyyy-MM'));
    cur = addMonths(cur, 1);
  }
  return months;
};

/**
 * Builds month labels (`yyyy-MM`) for the active filter range when the API omits `refs.months`.
 */
export const getMonthRefsFromCompositionV3Filters = (
  filters: CompositionV3Filters
): string[] => {
  const { start_date, end_date } = filters;
  if (!start_date || !end_date) {
    return [];
  }
  return buildMonthRefsInclusive(start_date, end_date);
};

export const getCompositionV3MonthRefs = ({
  availableDateRange,
  filters,
}: {
  availableDateRange?: {
    month?: { min?: UTCDate | null; max?: UTCDate | null };
  } | null;
  filters: CompositionV3Filters;
}): string[] => {
  const systemMin = availableDateRange?.month?.min;
  const systemMax = availableDateRange?.month?.max;
  if (systemMin && systemMax) {
    return buildMonthRefsInclusive(
      getFormattedDateString(systemMin, 'month'),
      getFormattedDateString(systemMax, 'month')
    );
  }
  return getMonthRefsFromCompositionV3Filters(filters);
};
